import { formatAsCurrency } from "utils/formatter-utils/currency-formatter";
import { useAppDispatch, useAppSelector } from "state/hooks";
import { getGroupCurrencySymbol, getGroupState, setCurrency } from "state/slices/group-slice";
import { useCallback, useEffect, useState } from "react";
import { getRequest, postRequest } from "utils/httpClient";
import { useAuth } from "./auth";
import { getWalletPath, integratedGetWallet } from "utils/backend-path-builders";
import { getIntegratedAppState, IntegratedAppState } from "state/slices/integrated-app-slice";
import { UserGroupMembershipStatus } from "utils/userGroupMembershipStatus";
import store from "state/store";
import { postBalanceMessage } from "utils/integration/iframe-messages";
import { toast } from "react-toastify";
import { integratedPost } from "utils/integrated-app-http-client.ts";

interface WalletProps {
  contestEntryId?: string;
  groupId?: string;
}

// todo rename to use GroupWallet
const useWallet = ({ contestEntryId, groupId: propGroupId }: WalletProps = {}) => {
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const [balance, setBalance] = useState<number | undefined>();
  const [walletId, setWalletId] = useState("");
  const [loading, setLoading] = useState(false);
  const group = useAppSelector(getGroupState);
  const groupId = propGroupId || group.id;
  const groupStatus = group.status;
  const integratedAppState = useAppSelector(getIntegratedAppState);
  const state = store.getState();
  const groupSymbol = getGroupCurrencySymbol(state);

  const getWalletData = useCallback(() => {
    switch (integratedAppState.state) {
      case IntegratedAppState.UNDEFINED:
        return;
      case IntegratedAppState.NOT_INTEGRATED:
        if ((auth.signedIn && ((groupId !== "" && groupStatus === UserGroupMembershipStatus.APPROVED) || propGroupId)) || contestEntryId) {
          const walletPath = contestEntryId
            ? `${getWalletPath(groupId)}?contestEntryId=${contestEntryId}`
            : getWalletPath(groupId);

          setLoading(true);
          getRequest(walletPath)
            .then((data) => {
              setBalance(data.current_balance || 0);
              setWalletId(data.wallet_id);
            })
            .catch((e) => {
              toast.error("Failed to fetch balance.");
              console.log("failed to fetch balance", e);
            })
            .finally(() => setLoading(false));
        }
        break;
      case IntegratedAppState.INTEGRATED_ANONYMOUS:
        break;
      case IntegratedAppState.INTEGRATED_SESSION_INITIALIZING:
        break;
      case IntegratedAppState.INTEGRATED_SESSION_INITIALIZED:
        setLoading(true);
        integratedPost(integratedGetWallet(contestEntryId), {})
          .then((response) => {
            postBalanceMessage(response.total);
            setBalance(response.total);
            setWalletId(response.wallet_id);
            dispatch(setCurrency(response.currency));
          })
          .catch((e) => {
            toast.error("Unable to fetch balance.");
            console.log("failed to fetch balance", e);
          })
          .finally(() => setLoading(false));
        break;
    }
  }, [
    auth.signedIn,
    groupId,
    groupStatus,
    contestEntryId,
    integratedAppState.state,
    propGroupId
  ]);

  useEffect(() => {
    getWalletData();
  }, [getWalletData]);

  let formattedBalance = groupSymbol + " --.--";
  if (balance !== undefined) {
    formattedBalance = formatAsCurrency(balance!);
  }

  return { balance, walletId, loading, formattedBalance, getWalletData };
};

export default useWallet;
